import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { AuthHelperService } from './auth-helper.service';
import { RouterService } from './router.service';
import { AccessIdService } from '../services/access-id.service';

@Injectable({
  providedIn: 'root',
})
export class TokenService {
  constructor(
    private authHelperService: AuthHelperService,
    private accessIdService: AccessIdService
  ) { }

  private accessTokenStorageKey = 'accessToken';

  private requestingToken = false;
  private tokenCallbacks: ((token: string | undefined) => void)[] = [];

  public setToken(token: string): void {
    localStorage.setItem(this.accessTokenStorageKey, token);
  }

  public removeToken(): void {
    localStorage.removeItem(this.accessTokenStorageKey);
  }

  public async getToken(): Promise<string | undefined> {
    const token = localStorage.getItem(this.accessTokenStorageKey) ?? undefined;
    const accessId = this.accessIdService.currentAccessId();
    if (!accessId) return;
    if (token && !this.isTokenExpired(token)) {
      return token;
    } else {
      return await new Promise<string | undefined>((resolve) => {
        this.resolveToken(resolve);
      });
    }
  }
  private async resolveToken(callback: (token: string | undefined) => void) {
    if (this.requestingToken) {
      this.tokenCallbacks.push(callback);
    } else {
      this.requestingToken = true;
      try {
        const refreshedToken = await this.authHelperService.startRefresh();
        this.resolveCallbacks(refreshedToken, callback);
      } finally {
        this.requestingToken = false;
        this.resolveCallbacks(undefined, callback);
        return;
      }
    }
  }

  private resolveCallbacks(
    token: string | undefined,
    callback: (token: string | undefined) => void,
  ) {
    callback(token);
    this.tokenCallbacks.forEach((cb) => cb(token));
    this.tokenCallbacks = [];
  }

  private isTokenExpired(token: string): boolean {
    try {
      const milliSeconds = 1000;
      const decodedToken = jwtDecode(token);
      if (!decodedToken.exp) return false;
      // Multiply by 1000 to convert seconds of token.exp to milliseconds
      const expiryDate = new Date(decodedToken.exp * milliSeconds).getTime();
      return Date.now() > expiryDate;
    } catch (e) {
      console.error('Error while checking token expiry', e);
      return true;
    }
  }
}
